import React, { FC, useCallback, useEffect, useState, useContext } from 'react';
import { TasksContext, TasksDispatchContext, TasksProvider, useTask } from '../stateManagement/context';
import { get, post, upload, download } from "../fetchers/fetchers";

import { useParams } from "react-router";

import {
    Button,
    Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, TextField,
    Tooltip,
    Typography,
    withStyles
} from "@material-ui/core";
import { IAssessment, IDocument, IProjectFailureMode, IRiskAssessment, IUpload } from "../types/types";
import RiskAssessment from "./risk_assessment";
import AddIcon from "@material-ui/icons/Add";
import { CloudUpload, PhotoCamera } from "@material-ui/icons";
import DialogContentText from "@material-ui/core/DialogContentText";
import Documents from "./documents";
import Dam from "./dam";
import AddRiskAssessmentDialog from "./add_risk_assessment_dialog";

import {getCriticalRiskAssessments, getCriticalAssessments} from "../stateManagement/data";
import { API_URL } from "../env";


interface IRiskAssessmenetsProps {
}

interface IRouteParams {
    id: string
}



const AddingButton = withStyles(theme => ({
    root: {
        margin: "auto",
        backgroundColor: "rgba(0,103,114,0.2)",
        color: "rgba(0,103,114,1)",
        '&:hover': {
            backgroundColor: "rgba(0,103,114,0.5)",
        },
    },
}))(IconButton);

const RiskAssessmenets: FC<IRiskAssessmenetsProps> = () => {

    const params = useParams<IRouteParams>()

    let dispatch = useContext(TasksDispatchContext)
    let tasks = useTask()

    const [isLoaded, setIsLoaded] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [failureModes, setFailureModes] = useState<IProjectFailureMode[]>([]);
    const [riskAssessments, setRiskAssessments] = useState<IRiskAssessment[]>([]);
    const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
    const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileID, setFileID] = useState<IUpload>();
    const [documentRef, setDocumentRef] = useState<IDocument>();
    const [accessLevel, setAccessLevel] = useState<number>(0);
    const [write_access, setWriteAccess] = useState<boolean>(false);


    useEffect(() => {
        setWriteAccess(tasks.write_access)
    },[tasks])

    useEffect(() => {

        if (tasks.loaded) {

            setFailureModes(tasks.failureModes)
            setRiskAssessments(tasks.criticalRiskAssessments)
            setAccessLevel(tasks.access_level)
        }

    },[tasks])

    const updateDocumentSelection = useCallback((documentRef: IDocument) => {
        setDocumentRef(documentRef)

    }, [setDocumentRef]);

    const addDialogCallback = useCallback(() => {
        setOpenAddDialog(false)

    }, [setOpenAddDialog]);

    const postAssessment = async (risk_assessment: IRiskAssessment) => {
        let ra = { ...risk_assessment };
        delete ra.assessment_project_id;
        delete ra.document;
        delete ra.id;
        delete ra.project_failure_mode;

        const d = await post<IRiskAssessment>(API_URL + "risk_assessments/" + params.id + "/risk_assessment", ra)
        
        getCriticalRiskAssessments(params.id, dispatch)

        // This needs to be triggered after the grouping are updated
        getCriticalAssessments(params.id, dispatch)
    }

    const updateRiskAssessment = useCallback((assessment: IRiskAssessment) => {

        postAssessment(assessment).then(() => { setIsLoaded(false); })
    }, [postAssessment, setIsLoaded]);

    const postAssessments = async () => {
        if (fileID) {
            const d = await post<IUpload>(API_URL + "risk_assessments/" + params.id + "/file_upload", {
                uuid: fileID.uuid,
                name: fileID.name,
                document_id: documentRef?.id
            });
        }
    }

    const submitAssessments = () => {
        if (fileID) {
            postAssessments().then(() => {
                setIsLoaded(false)
                setOpenCreateDialog(false)
            })
        }

    }


    const uploadFile = async (file: any) => {
        const d = await upload<IUpload>(API_URL + "upload/", file);
        setFileID(d)
    }

    const handleCapture = ({ target }: any) => {
        let formData = new FormData();
        formData.append("file", target.files[0]);
        uploadFile(formData).then(() => setSelectedFile(target.files[0].name))
    };

    const handleClose = () => {
        setOpenCreateDialog(false)
    }
    const handleOpen = () => {
        setOpenCreateDialog(true)
    }

    const handleAdd = () => {
        setOpenAddDialog(true)
    }
    const handleAddClose = () => {
        setOpenAddDialog(false)
    }


    const create_upload_dialog = () => {
        return (
            <Dialog open={openCreateDialog} onClose={handleClose} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">Upload Risk Assessments</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        <Typography>
                            Upload Risk Assessments from a csv file.
                            The csv file needs to have following headers:<br />
                            <small>Failure Mode ID,Description,Total Annual Probability of Failure,Societal Risk,Individual Risk</small>
                        </Typography>

                        <p />
                        {!selectedFile && <div>
                            <input
                                accept=".csv"
                                id="icon-button-upload"
                                type="file"
                                hidden
                                onChange={handleCapture}
                            />

                            <label htmlFor="icon-button-upload">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component="span"
                                    startIcon={<CloudUpload />}
                                    onClick={() => handleOpen()}
                                >
                                    Upload
                                </Button>
                            </label>

                        </div>}
                        {selectedFile && <div>
                            {selectedFile}
                        </div>}
                        <p />

                        <Documents updateDocumentID={updateDocumentSelection} />

                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                    {selectedFile && <Button onClick={submitAssessments} color="primary">
                        Submit
                    </Button>}
                </DialogActions>
            </Dialog>
        )
    }


    return (
        < >
            
            
            {write_access &&
            <>
            <Tooltip title="Add Assessment" placement={'bottom-end'}>
                <AddingButton style={{ backgroundColor: "rgb(239,88,43)", color: "#FFFFFF" }} onClick={prevState => handleAdd()} >
                    <AddIcon fontSize="small" />
                </AddingButton>
            </Tooltip>
            

            <IconButton color="primary" component="span"
                style={{
                    margin: "auto",
                    backgroundColor: "rgb(239,88,43)",
                    color: "rgb(255,255,255)"
                }} onClick={() => handleOpen()}>
                <CloudUpload fontSize="small" />
            </IconButton>
            </>
            }


            <Table>
                <TableHead style={{ position: 'sticky', top: 65, zIndex: 2, backgroundColor: "#ffffff" }}>
                    <TableRow>
                        <TableCell>Failure Mode</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Total Annual Probability of Failure</TableCell>
                        <TableCell align="right">Societal Risk</TableCell>
                        <TableCell align="right">Individual Risk</TableCell>
                        <TableCell>Group Failure Mode</TableCell>
                        <TableCell>Reference</TableCell>
                        <TableCell>Comments</TableCell>
                        <TableCell>Controls</TableCell>
                    </TableRow>

                </TableHead>

                {riskAssessments.map((r) => {
                    return (
                        <RiskAssessment failure_modes={failureModes} risk_assessment={r} updateRiskAssessment={updateRiskAssessment} />
                    )
                })}
            </Table>


            {create_upload_dialog()}

            {openAddDialog &&
                <AddRiskAssessmentDialog risk_assessment={{
                    societal_risk: 0,
                    annual_failure_probability: 0,
                    individual_risk: 0
                }}
                    updateRiskAssessmentCallback={updateRiskAssessment}
                    closeDialogCallback={addDialogCallback} />
            }

        </ >
    );
}

export default RiskAssessmenets;

